import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import Compressor from 'compressorjs';

import AuthService from './AuthService';
import withAuth from './WithAuth';
import { buildConditionsList, LEGACY_CONDITIONS, LEGACY_NO_FOIL_RARITIES } from '../utils/conditions';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { formatSealedType } from '../utils/sealedTypes';

var Auth = new AuthService();
var SHARE_MIN_PRICE = constants.SHARE_MIN_PRICE || 10;

var ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
var MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

class BulkPost extends Component {
  constructor() {
    super();
    this.state = {
      step: 1,
      batch: [],
      currentCardIndex: 0,
      submitting: false,
      submitError: '',
      results: null,
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      showDropdown: false,
      photoError: '',
      processingPhoto: false,
      hasPayoutMethod: null,
      bulkMode: 'single', // 'single' or 'sealed'
    };
    this.debounceTimer = null;
    this.searchInputRef = React.createRef();
  }

  componentDidMount() {
    if (this.searchInputRef.current) {
      this.searchInputRef.current.focus();
    }
    // Theme listener
    this._handleThemeChanged = function () { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
    // Check payout method
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/users/' + this.props.user._id, { mode: 'cors', method: 'GET' })
      .then(function (res) {
        var hasPayout = !!(res.profile.stripeAccountId || res.profile.paypalEmail);
        self.setState({ hasPayoutMethod: hasPayout });
      })
      .catch(function () {
        self.setState({ hasPayoutMethod: false });
      });
  }

  componentDidUpdate() {
    document.body.style.overflow = this.state.results ? 'hidden' : '';
  }

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    window.removeEventListener('themeChanged', this._handleThemeChanged);
    document.body.style.overflow = '';
  }

  // ─── Search ─────────────────────────────────────────────

  handleSearchInput(e) {
    var query = e.target.value;
    var self = this;
    this.setState({ searchQuery: query });

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (query.trim().length < 2) {
      this.setState({ searchResults: [], showDropdown: false, searchLoading: false });
      return;
    }

    this.setState({ searchLoading: true });
    var productTypeParam = self.state.bulkMode === 'sealed' ? '&productType=sealed' : '';
    this.debounceTimer = setTimeout(function () {
      fetch(constants.DOMAIN + '/api/v1/guest/cards/search?search=' + encodeURIComponent(query.trim()) + '&limit=20' + productTypeParam, {
        mode: 'cors',
        method: 'GET',
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          self.setState({ searchResults: res.cards || [], showDropdown: true, searchLoading: false });
        })
        .catch(function () { self.setState({ searchLoading: false }); });
    }, 400);
  }

  addCardToBatch(card) {
    var cardImg = resolveCardImage(card.image);
    var isSealed = this.state.bulkMode === 'sealed';
    this.setState(function (prev) {
      return {
        batch: prev.batch.concat([{
          card: card,
          cardImage: cardImg,
          title: card.name + (!isSealed && card.variant ? ' (' + card.variant + ')' : ''),
          description: isSealed
            ? [card.cardSet, formatSealedType(card.sealedType)].filter(Boolean).join(' - ')
            : [card.cardSet, card.rarity].filter(Boolean).join(' - '),
          condition: isSealed ? null : 'NM',
          price: card.lowestPrice ? Number(card.lowestPrice).toFixed(2) : '',
          quantity: 1,
          allowSharing: false,
          shareCommission: 1,
          shareCommissionType: 'amount',
          acceptOffers: false,
          photos: [],
          pricingData: null,
          priceTouched: false,
          titleTouched: false,
          isSealed: isSealed,
        }]),
        showDropdown: false,
        searchQuery: '',
      };
    });
  }

  updateBatchItem(index, field, value) {
    this.setState(function (prev) {
      var batch = prev.batch.slice();
      var item = Object.assign({}, batch[index]);
      item[field] = value;
      batch[index] = item;
      return { batch: batch };
    });
  }

  removeBatchItem(index) {
    this.setState(function (prev) {
      return { batch: prev.batch.filter(function (_, i) { return i !== index; }) };
    });
  }

  handlePriceInput(index, value) {
    var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
    if (value === '' || re.test(value)) {
      this.updateBatchItem(index, 'price', value);
    }
  }

  // ─── Pricing ────────────────────────────────────────────

  fetchPricing(cardId, batchIndex) {
    var self = this;
    fetch(constants.DOMAIN + '/api/v1/guest/card/' + cardId + '/pricing', { mode: 'cors' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        self.updateBatchItem(batchIndex, 'pricingData', data.pricing || null);
      })
      .catch(function () {
        // silently ignore pricing errors
      });
  }

  renderPricingWidget(item) {
    var pricingData = item.pricingData;
    var condition = item.condition;
    if (!pricingData || Object.keys(pricingData).length === 0) return null;
    var isDark = document.documentElement.classList.contains('tw-dark');

    var condLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === condition; });
    var condName = condLabel ? condLabel.label : condition;
    var data = pricingData[condition];
    var fallback = false;
    var fallbackCondName = '';

    if (!data) {
      var bestCond = null;
      var bestCount = 0;
      Object.keys(pricingData).forEach(function (key) {
        if (pricingData[key].count > bestCount) {
          bestCount = pricingData[key].count;
          bestCond = key;
        }
      });
      if (!bestCond) return null;
      data = pricingData[bestCond];
      fallback = true;
      var fbLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === bestCond; });
      fallbackCondName = fbLabel ? fbLabel.label : bestCond;
    }

    var sourceText = data.count + ' ' + (data.source === 'sales' ? (data.count === 1 ? 'sale' : 'sales') : (data.count === 1 ? 'listing' : 'listings'));

    return (
      <div className="tw-mb-4 tw-rounded-xl tw-p-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f9fafb', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
        <div>
          {fallback ? (
            <div className="tw-mb-2">
              <p className="tw-text-xs tw-text-[#888] tw-mb-1 tw-mt-0">No {condName} data</p>
              <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-0" style={{ color: isDark ? '#ccc' : '#555' }}>
                {fallbackCondName} prices ({sourceText}):
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-0" style={{ color: isDark ? '#ccc' : '#555' }}>{condName} Prices</p>
              <p className="tw-text-[10px] tw-text-[#999] tw-mb-0 tw-mt-0">Based on {sourceText}</p>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-text-center">
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Low</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.low.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Median</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.median.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">High</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.high.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Photos ─────────────────────────────────────────────

  getImageDimensions(image, obj) {
    return new Promise(function (resolve) {
      var fr = new FileReader();
      fr.onload = function () {
        var img = new Image();
        img.onload = function () {
          obj.width = img.width;
          resolve(obj);
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(image);
    });
  }

  compressionWidth(width) {
    if (width > constants.MAX_IMAGE_WIDTH) return constants.MAX_IMAGE_WIDTH;
    if (width >= constants.MIN_IMAGE_WIDTH) return Math.round(width * 0.8);
    return width;
  }

  async addPhoto(event) {
    var inputEl = event.target;
    var file = inputEl.files[0];
    var idx = this.state.currentCardIndex;
    var currentPhotos = this.state.batch[idx] ? this.state.batch[idx].photos : [];
    if (!file || currentPhotos.length >= 4) return;

    if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
      this.setState({ photoError: 'Unsupported format. Use JPG, PNG, GIF, or WebP.' });
      inputEl.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.setState({ photoError: 'File too large. Max 10 MB.' });
      inputEl.value = '';
      return;
    }

    this.setState({ photoError: '', processingPhoto: true });
    var tmp = {};
    tmp = await this.getImageDimensions(file, tmp);
    var self = this;

    new Compressor(file, {
      quality: constants.IMAGE_COMPRESSION,
      maxWidth: this.compressionWidth(tmp.width),
      success(result) {
        var finalFile = result instanceof File
          ? result
          : new File([result], result.name, { type: result.type, lastModified: Date.now() });

        var reader = new FileReader();
        reader.onload = function (e) {
          self.setState(function (prev) {
            var batch = prev.batch.slice();
            var item = Object.assign({}, batch[idx]);
            item.photos = item.photos.concat([{ file: finalFile, preview: e.target.result }]);
            batch[idx] = item;
            return { batch: batch, processingPhoto: false };
          });
        };
        reader.readAsDataURL(finalFile);
      },
      error: function () {
        self.setState({ photoError: 'Could not process image. Try a JPG or PNG instead.', processingPhoto: false });
      },
    });
    inputEl.value = '';
  }

  removePhoto(cardIndex, photoIndex) {
    this.setState(function (prev) {
      var batch = prev.batch.slice();
      var item = Object.assign({}, batch[cardIndex]);
      item.photos = item.photos.filter(function (_, i) { return i !== photoIndex; });
      batch[cardIndex] = item;
      return { batch: batch };
    });
  }

  // ─── Navigation ─────────────────────────────────────────

  goToStep(step) {
    this.setState({ step: step, submitError: '' });
  }

  goToDetails() {
    if (this.state.batch.length === 0) return;
    var firstItem = this.state.batch[0];
    if (!firstItem.pricingData) {
      this.fetchPricing(firstItem.card._id, 0);
    }
    this.setState({ step: 2, currentCardIndex: 0 });
  }

  goToNextDetailCard() {
    var batch = this.state.batch;
    var idx = this.state.currentCardIndex;

    // Validate current card before advancing
    var item = batch[idx];
    if (!item.price || isNaN(Number(item.price)) || Number(item.price) < 0.75) {
      this.setState({ submitError: 'Minimum price is $0.75' });
      return;
    }
    this.setState({ submitError: '' });

    if (idx < batch.length - 1) {
      var nextIdx = idx + 1;
      if (!batch[nextIdx].pricingData) {
        this.fetchPricing(batch[nextIdx].card._id, nextIdx);
      }
      this.setState({ currentCardIndex: nextIdx });
    } else {
      // Last card → go to review step
      this.setState({ step: 3 });
    }
  }

  goToPrevDetailCard() {
    var idx = this.state.currentCardIndex;
    if (idx > 0) {
      this.setState({ currentCardIndex: idx - 1, submitError: '' });
    } else {
      this.setState({ step: 1, submitError: '' });
    }
  }

  goToNextPhotoCard() {
    var batch = this.state.batch;
    var idx = this.state.currentCardIndex;
    if (idx < batch.length - 1) {
      this.setState({ currentCardIndex: idx + 1, photoError: '' });
    } else {
      this.setState({ step: 3, photoError: '' });
    }
  }

  skipAllPhotos() {
    this.setState({ step: 3, photoError: '' });
  }

  editCardFromReview(index) {
    if (!this.state.batch[index].pricingData) {
      this.fetchPricing(this.state.batch[index].card._id, index);
    }
    this.setState({ step: 2, currentCardIndex: index, submitError: '' });
  }

  // ─── Submit ─────────────────────────────────────────────

  submitAll() {
    var batch = this.state.batch;
    var self = this;
    if (batch.length === 0) return;

    for (var i = 0; i < batch.length; i++) {
      if (!batch[i].price || isNaN(Number(batch[i].price)) || Number(batch[i].price) < 0.75) {
        this.setState({ submitError: 'Card "' + batch[i].title + '" has an invalid price. Minimum is $0.75.' });
        return;
      }
    }
    this.setState({ submitError: '', submitting: true });

    var withPhotos = [];
    var noPhotos = [];
    batch.forEach(function (item) {
      if (item.photos.length > 0) {
        withPhotos.push(item);
      } else {
        noPhotos.push(item);
      }
    });

    var token = Auth.getToken();
    var allResults = [];
    var allErrors = [];

    // Submit no-photo cards via bulk endpoint
    var bulkPromise;
    if (noPhotos.length > 0) {
      var listings = noPhotos.map(function (item) {
        var listing = {
          title: item.title,
          text: item.description,
          price: Number(item.price),
          shippingCost: 0,
          cards: [item.card._id],
          cardImage: item.cardImage,
          quantity: Number(item.quantity) || 1,
          allowSharing: item.allowSharing !== false,
          shareCommission: item.shareCommission || 5,
          acceptOffers: item.acceptOffers === true,
        };
        if (item.isSealed) {
          listing.productType = 'sealed';
          listing.category = 'sealed';
          listing.condition = null;
        } else {
          listing.category = 'singles ' + item.condition;
          listing.condition = item.condition;
        }
        return listing;
      });

      bulkPromise = fetch(constants.DOMAIN + '/api/v1/posts/bulk', {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': Auth.getAuthHeader(),
        },
        body: JSON.stringify({ listings: listings }),
      }).then(function (r) {
        if (r.status >= 200 && r.status < 300) return r.json();
        throw new Error('Bulk request failed');
      }).then(function (res) {
        if (res.results) allResults = allResults.concat(res.results);
        if (res.errors) allErrors = allErrors.concat(res.errors);
      });
    } else {
      bulkPromise = Promise.resolve();
    }

    // Submit photo cards individually via single-post endpoint
    var photoPromises = withPhotos.map(function (item) {
      var data = new FormData();
      data.append('title', item.title);
      data.append('text', item.description || '');
      data.append('price', Number(item.price));
      if (item.isSealed) {
        data.append('productType', 'sealed');
        data.append('category', 'sealed');
      } else {
        data.append('category', 'singles ' + item.condition);
        data.append('condition', item.condition);
        data.append('foil', item.condition && item.condition.endsWith('F'));
      }
      data.append('shippingCost', 0);
      data.append('quantity', Number(item.quantity) || 1);
      data.append('allowSharing', item.allowSharing !== false);
      data.append('shareCommission', Math.max(1, Number(item.shareCommission) || 1));
      data.append('shareCommissionType', 'amount');
      data.append('acceptOffers', item.acceptOffers === true);
      data.append('cards', item.card._id);
      if (item.cardImage) data.append('cardImage', item.cardImage);
      item.photos.forEach(function (p) {
        data.append('images', p.file, p.file.name);
      });

      return fetch(constants.DOMAIN + '/api/v1/posts', {
        mode: 'cors',
        method: 'POST',
        headers: { 'Authorization': Auth.getAuthHeader() },
        body: data,
      }).then(function (r) {
        if (r.status >= 200 && r.status < 300) return r.json();
        throw new Error('Single post failed');
      }).then(function (res) {
        allResults.push(res);
      }).catch(function () {
        allErrors.push({ title: item.title });
      });
    });

    // Chain: bulk first, then individual photo posts sequentially
    var chain = bulkPromise;
    photoPromises.forEach(function (p) {
      chain = chain.then(function () { return p; });
    });

    chain.then(function () {
      self.setState({
        results: { results: allResults, errors: allErrors },
        submitting: false,
      });
    }).catch(function (err) {
      console.log(err);
      self.setState({ submitting: false, submitError: 'Failed to submit listings. Please try again.' });
    });
  }

  // ─── Step Indicator ─────────────────────────────────────

  renderStepIndicator() {
    var step = this.state.step;
    var steps = ['Add Cards', 'Details', 'Review'];
    var items = [];
    var self = this;

    for (var i = 0; i < steps.length; i++) {
      var num = i + 1;
      var active = num === step;
      var done = num < step;

      if (i > 0) {
        items.push(
          <div
            key={'line-' + i}
            className="tw-h-0.5 tw-flex-1"
            style={{ backgroundColor: done ? '#c9a84c' : '#e0e0e0', maxWidth: '48px' }}
          />
        );
      }

      items.push(
        <div
          key={num}
          className={'tw-flex tw-flex-col tw-items-center tw-gap-1' + (done ? ' tw-cursor-pointer' : '')}
          onClick={done ? (function (s) { return function () { self.setState({ step: s }); }; })(num) : undefined}
        >
          <div
            className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold"
            style={
              active ? { backgroundColor: '#c9a84c', color: '#000', border: '2px solid #c9a84c' } :
              done ? { backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '2px solid #c9a84c' } :
              { backgroundColor: 'transparent', color: '#999', border: '2px solid #e0e0e0' }
            }
          >
            {done ? '\u2713' : num}
          </div>
          <span
            className={'tw-text-[10px] tw-font-medium' + (done ? ' hover:tw-underline' : '')}
            style={{ color: active || done ? '#c9a84c' : '#999' }}
          >
            {steps[i]}
          </span>
        </div>
      );
    }

    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-8">
        {items}
      </div>
    );
  }

  // ─── Step 1: Add Cards ──────────────────────────────────

  renderStep1() {
    var searchQuery = this.state.searchQuery;
    var searchResults = this.state.searchResults;
    var searchLoading = this.state.searchLoading;
    var showDropdown = this.state.showDropdown;
    var batch = this.state.batch;
    var self = this;

    var isDark = document.documentElement.classList.contains('tw-dark');
    var inputStyle = {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      borderRadius: '8px',
      border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
      outline: 'none',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#1a1a1a',
    };

    return (
      <div>
        <div className="tw-text-center tw-mb-6">
          <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            {self.state.bulkMode === 'sealed' ? 'Add Sealed Products to Sell' : 'Add Cards to Sell'}
          </h2>
          <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
            {self.state.bulkMode === 'sealed' ? 'Search and add sealed products to your batch' : 'Search and add cards to your batch'}
          </p>
        </div>

        {/* Single / Sealed toggle */}
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-mb-6 tw-p-1 tw-rounded-lg" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f0f0f0' }}>
          <button
            type="button"
            onClick={function () { if (self.state.bulkMode !== 'single') self.setState({ bulkMode: 'single', batch: [], searchQuery: '', searchResults: [], showDropdown: false }); }}
            className="tw-flex-1 tw-text-sm tw-font-semibold tw-py-2 tw-px-4 tw-rounded-md tw-border-none tw-cursor-pointer tw-transition-all"
            style={self.state.bulkMode === 'single'
              ? { backgroundColor: '#c9a84c', color: '#000' }
              : { backgroundColor: 'transparent', color: isDark ? '#999' : '#666' }}
          >
            <i className="fa fa-clone tw-mr-1.5" />Singles
          </button>
          <button
            type="button"
            onClick={function () { if (self.state.bulkMode !== 'sealed') self.setState({ bulkMode: 'sealed', batch: [], searchQuery: '', searchResults: [], showDropdown: false }); }}
            className="tw-flex-1 tw-text-sm tw-font-semibold tw-py-2 tw-px-4 tw-rounded-md tw-border-none tw-cursor-pointer tw-transition-all"
            style={self.state.bulkMode === 'sealed'
              ? { backgroundColor: '#7c3aed', color: '#fff' }
              : { backgroundColor: 'transparent', color: isDark ? '#999' : '#666' }}
          >
            <i className="fa fa-cube tw-mr-1.5" />Sealed
          </button>
        </div>

        {/* Search bar */}
        <div className="tw-relative tw-mb-5">
          <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            {self.state.bulkMode === 'sealed' ? 'Search sealed products to add' : 'Search cards to add'}
          </label>
          <div className="tw-relative">
            <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-[#999]" style={{ transform: 'translateY(-50%)' }}>
              <i className={'fa ' + (self.state.bulkMode === 'sealed' ? 'fa-cube' : 'fa-search') + ' tw-text-sm'} />
            </span>
            <input
              ref={self.searchInputRef}
              type="text"
              value={searchQuery}
              onChange={function (e) { self.handleSearchInput(e); }}
              placeholder={self.state.bulkMode === 'sealed' ? 'Search sealed catalog...' : 'Type a card name...'}
              style={Object.assign({}, inputStyle, { paddingLeft: '32px' })}
              className="focus:tw-border-brand-gold"
            />
            {searchLoading && (
              <span className="tw-absolute tw-right-3 tw-top-1/2 tw-text-xs tw-text-[#999]" style={{ transform: 'translateY(-50%)' }}>
                Searching...
              </span>
            )}
          </div>

          {/* Search dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div
              className="tw-absolute tw-left-0 tw-right-0 tw-rounded-xl tw-mt-1 tw-shadow-xl tw-z-50 tw-overflow-hidden"
              style={{ maxHeight: '320px', overflowY: 'auto', backgroundColor: isDark ? '#1a1a1a' : '#fff', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
            >
              {searchResults.map(function (card) {
                var cardImg = resolveCardImage(card.image);
                return (
                  <button
                    key={card._id}
                    type="button"
                    onClick={function () { self.addCardToBatch(card); }}
                    className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-left tw-transition-colors"
                    style={{ borderBottom: isDark ? '1px solid #252525' : '1px solid #f0f0f0' }}
                    onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = isDark ? '#252525' : '#f5f5f5'; }}
                    onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {cardImg && (
                      <img src={cardImg} alt={card.name} className="tw-w-8 tw-rounded tw-flex-shrink-0" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
                    )}
                    <div className="tw-flex-1 tw-min-w-0">
                      <p className="tw-text-sm tw-font-medium tw-mb-0 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                        {card.name}
                        {card.variant ? ' (' + card.variant + ')' : ''}
                      </p>
                      <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">
                        {card.cardSet || ''}
                        {self.state.bulkMode === 'sealed' && card.sealedType
                          ? ' \u2022 ' + ({ booster_box: 'Booster Box', booster_pack: 'Booster Pack', starter_deck: 'Starter Deck', anniversary_box: 'Anniversary Box' }[card.sealedType] || card.sealedType)
                          : (card.rarity ? ' \u2022 ' + card.rarity : '')}
                      </p>
                    </div>
                    {card.lowestPrice > 0 && (
                      <span className="tw-text-xs tw-font-bold tw-flex-shrink-0" style={{ color: '#c9a84c' }}>
                        ${Number(card.lowestPrice).toFixed(2)}
                      </span>
                    )}
                    <i className="fa fa-plus-circle tw-text-[#ccc] tw-flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Batch list */}
        {batch.length > 0 ? (
          <div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
              {batch.map(function (item, i) {
                return (
                  <div
                    key={i}
                    className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-xl tw-transition-colors"
                    style={{ backgroundColor: isDark ? '#141414' : '#fafafa', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', transition: 'border-color 0.15s ease' }}
                    onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                    onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
                  >
                    {item.cardImage && (
                      <img src={item.cardImage} alt={item.title} className="tw-w-10 tw-rounded tw-flex-shrink-0" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
                    )}
                    <div className="tw-flex-1 tw-min-w-0">
                      <p className="tw-text-sm tw-font-semibold tw-mb-0 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                        {item.title}
                      </p>
                      <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">
                        {item.card.cardSet || ''}
                        {item.isSealed && item.card.sealedType
                          ? ' \u2022 ' + ({ booster_box: 'Booster Box', booster_pack: 'Booster Pack', starter_deck: 'Starter Deck', anniversary_box: 'Anniversary Box' }[item.card.sealedType] || item.card.sealedType)
                          : (item.card.rarity ? ' \u2022 ' + item.card.rarity : '')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={function () { self.removeBatchItem(i); }}
                      className="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[#ccc] hover:tw-text-red-500 tw-transition-colors tw-flex-shrink-0"
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={function () { self.goToDetails(); }}
              className="tw-w-full tw-font-bold tw-py-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none"
              style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
              onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
              onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="tw-text-center tw-py-16">
            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-mx-auto tw-mb-4 tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5' }}>
              <i className="fa fa-th-list tw-text-xl tw-text-[#ccc]" />
            </div>
            <p className="tw-text-sm tw-text-[#999] tw-mb-1 tw-font-medium">
              {self.state.bulkMode === 'sealed' ? 'No sealed products added yet' : 'No cards added yet'}
            </p>
            <p className="tw-text-xs tw-text-[#ccc]">
              {self.state.bulkMode === 'sealed' ? 'Search above and click a product to add it to your batch' : 'Search above and click a card to add it to your batch'}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ─── Step 2: Details (per card) ─────────────────────────

  renderStep2() {
    var batch = this.state.batch;
    var idx = this.state.currentCardIndex;
    var item = batch[idx];
    if (!item) return null;

    var cardRarity = item.card ? item.card.rarity : null;
    var conditionsList = buildConditionsList(null, LEGACY_NO_FOIL_RARITIES, cardRarity);
    var self = this;

    var isDark = document.documentElement.classList.contains('tw-dark');
    var inputStyle = {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      borderRadius: '8px',
      border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
      outline: 'none',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#1a1a1a',
    };

    return (
      <div>
        {/* Navigation */}
        <button
          type="button"
          onClick={function () { self.goToPrevDetailCard(); }}
          className="tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-mb-5 tw-p-0"
          style={{ color: '#999', transition: 'color 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={function (e) { e.currentTarget.style.color = '#999'; }}
        >
          &larr; {idx === 0 ? 'Back to card list' : 'Previous card'}
        </button>

        {/* Card counter */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
          <h2 className="tw-text-lg tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            Card {idx + 1} of {batch.length}
          </h2>
          {batch.length <= 20 && (
            <div className="tw-flex tw-gap-1">
              {batch.map(function (_, i) {
                return (
                  <div
                    key={i}
                    className="tw-w-2 tw-h-2 tw-rounded-full"
                    style={{ backgroundColor: i === idx ? '#c9a84c' : (i < idx ? 'rgba(201,168,76,0.4)' : '#e0e0e0') }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Card preview */}
        {item.cardImage && (
          <div className="tw-flex tw-items-center tw-gap-4 tw-mb-6 tw-p-4 tw-rounded-xl" style={{ backgroundColor: isDark ? '#141414' : '#f9fafb', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}>
            <img src={item.cardImage} alt={item.card.name} className="tw-w-16 tw-rounded" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-sm tw-font-bold tw-mb-0.5 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                {item.card.name}
                {item.card.variant ? ' (' + item.card.variant + ')' : ''}
              </p>
              <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
                {item.card.cardSet}
                {item.card.rarity ? ' \u2022 ' + item.card.rarity : ''}
              </p>
              {item.card.lowestPrice > 0 && (
                <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-1" style={{ color: '#c9a84c' }}>
                  Market: ${Number(item.card.lowestPrice).toFixed(2)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Guide */}
        {this.renderPricingWidget(item)}

        {/* Price + Quantity side-by-side */}
        <div className="tw-flex tw-gap-3 tw-mb-4">
          <div className="tw-flex-1">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Price</label>
            {(function () {
              var priceValid = item.price !== '' && Number(item.price) >= 0.75;
              var priceTouched = item.priceTouched;
              var borderColor = priceTouched ? (priceValid ? '#22c55e' : '#ef4444') : (isDark ? '#555' : '#e0e0e0');
              return (
                <div style={{ position: 'relative' }}>
                  <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)', zIndex: 1 }}>$</span>
                  <input
                    type="text"
                    value={item.price}
                    onChange={function (e) { self.handlePriceInput(idx, e.target.value); }}
                    onBlur={function () { self.updateBatchItem(idx, 'priceTouched', true); }}
                    placeholder="0.00"
                    style={Object.assign({}, inputStyle, { paddingLeft: '24px', paddingRight: '32px', borderColor: borderColor })}
                    className="focus:tw-border-brand-gold"
                  />
                  {priceTouched && (
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: priceValid ? '#22c55e' : '#ef4444', pointerEvents: 'none' }}>
                      {priceValid ? '\u2713' : '\u2717'}
                    </span>
                  )}
                </div>
              );
            })()}
            {self.state.submitError && (
              <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{self.state.submitError}</p>
            )}
          </div>
          <div style={{ width: '100px' }}>
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Qty</label>
            <input
              type="number"
              min="1"
              max="999"
              value={item.quantity || 1}
              onChange={function (e) {
                var val = e.target.value;
                if (val === '' || (Number(val) >= 1 && Number(val) <= 999)) {
                  self.updateBatchItem(idx, 'quantity', val);
                }
              }}
              style={inputStyle}
              className="focus:tw-border-brand-gold"
            />
          </div>
        </div>
        <p className="tw-text-xs tw-text-[#999] tw-mt-0 tw-mb-2">
          <i className="fa fa-truck tw-mr-1" style={{ color: '#c9a84c' }}></i>
          Shipping: $1.00 flat rate (free on orders $10+). Tracking required at $50+. &mdash; <Link to="/shipping" className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline">Shipping Guide</Link>
        </p>
        {(function () {
          var p = Number(item.price);
          if (!p || p < 0.75) return null;
          var shipping = p >= 10 ? 0 : 1.00;
          var sellerAmt = (p * 0.90) + shipping;
          var payment = p + shipping + 0.50;
          var net = payment - sellerAmt;
          var procFee = (payment * 0.029) + 0.30;
          var due = net - procFee;
          if (due < 0.75) {
            var shortfall = due <= 0 ? 0.75 : 0.75 - due;
            sellerAmt = sellerAmt - shortfall;
          }
          var payout = Math.floor(sellerAmt * 100) / 100;
          var feeAmt = Math.round((p + shipping - payout) * 100) / 100;
          return (
            <div className="tw-rounded-lg tw-p-2.5 tw-mb-4" style={{ backgroundColor: isDark ? 'rgba(22,163,106,0.08)' : 'rgba(22,163,106,0.05)', border: '1px solid ' + (isDark ? 'rgba(22,163,106,0.2)' : 'rgba(22,163,106,0.15)') }}>
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-xs" style={{ color: isDark ? '#aaa' : '#666' }}>
                  <i className="fa fa-info-circle tw-mr-1" style={{ color: '#c9a84c' }}></i>
                  Estimated payout each
                </span>
                <span className="tw-text-sm tw-font-bold" style={{ color: '#16a34a' }}>${payout.toFixed(2)}</span>
              </div>
              <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                Platform fee: ${feeAmt.toFixed(2)}{shipping > 0 ? ' \u00b7 Shipping passed to you in full' : ''}
              </p>
              {feeAmt / p > 0.11 ? (
                <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                  Fee is lower when buyers purchase multiple cards from you. <a href="/faq#payout-fees" target="_blank" rel="noopener noreferrer" className="tw-no-underline" style={{ color: '#c9a84c' }}>Learn more</a>
                </p>
              ) : null}
            </div>
          );
        })()}

        {/* Condition (hidden for sealed products) */}
        {!item.isSealed && (
        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Condition/Foil</label>
          <select
            value={item.condition}
            onChange={function (e) { self.updateBatchItem(idx, 'condition', e.target.value); }}
            style={inputStyle}
            className="focus:tw-border-brand-gold"
          >
            {(function () {
              var nonFoil = conditionsList.filter(function (c) { return !c.foilOnly; });
              var foil = conditionsList.filter(function (c) { return c.foilOnly; });
              if (foil.length > 0) {
                return [
                  <optgroup key="nf" label="Non-Foil">
                    {nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                  </optgroup>,
                  <optgroup key="f" label="Foil">
                    {foil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                  </optgroup>
                ];
              }
              return nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; });
            })()}
          </select>
        </div>
        )}

        {/* Add Card Details — collapsible */}
        <div className="tw-mb-4">
          <button
            type="button"
            onClick={function () { self.updateBatchItem(idx, 'showCardDetails', !item.showCardDetails); }}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-font-semibold hover:tw-underline"
            style={{ color: '#c9a84c' }}
          >
            <i className={'fa ' + (item.showCardDetails ? 'fa-chevron-down' : 'fa-pencil')} style={{ fontSize: '12px' }} />
            {item.showCardDetails ? 'Hide card details' : 'Add card details'}
            <span className="tw-text-xs tw-font-normal tw-text-[#999]">(optional)</span>
          </button>
          {item.showCardDetails && (
            <div className="tw-mt-3">
              <div className="tw-mb-3">
                <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Title</label>
                {(function () {
                  var titleValid = item.title.trim().length > 0;
                  var titleTouched = item.titleTouched;
                  var borderColor = titleTouched ? (titleValid ? '#22c55e' : '#ef4444') : (isDark ? '#555' : '#e0e0e0');
                  return (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={item.title}
                        onChange={function (e) { self.updateBatchItem(idx, 'title', e.target.value); }}
                        onBlur={function () { self.updateBatchItem(idx, 'titleTouched', true); }}
                        style={Object.assign({}, inputStyle, { paddingRight: '32px', borderColor: borderColor })}
                        className="focus:tw-border-brand-gold"
                      />
                      {titleTouched && (
                        <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: titleValid ? '#22c55e' : '#ef4444', pointerEvents: 'none' }}>
                          {titleValid ? '\u2713' : '\u2717'}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Description</label>
                <textarea
                  value={item.description}
                  onChange={function (e) { self.updateBatchItem(idx, 'description', e.target.value); }}
                  rows={3}
                  maxLength={500}
                  style={Object.assign({}, inputStyle, { resize: 'none' })}
                  className="focus:tw-border-brand-gold"
                />
                <p style={{ fontSize: '12px', textAlign: 'right', margin: '4px 0 0', color: item.description.length >= 500 ? '#ef4444' : item.description.length >= 450 ? '#c9a84c' : '#999' }}>{item.description.length} / 500</p>
              </div>
            </div>
          )}
        </div>

        {/* Share & Earn */}
        {(function () {
          var bulkPriceTooLow = !item.price || Number(item.price) < SHARE_MIN_PRICE;
          var bulkSharingEnabled = item.allowSharing !== false && !bulkPriceTooLow;
          return (
        <div className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Share & Earn</label>
            <button
              type="button"
              onClick={function () { if (!bulkPriceTooLow) { self.updateBatchItem(idx, 'allowSharing', !item.allowSharing); } }}
              className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-transition-colors tw-flex-shrink-0"
              style={{ backgroundColor: bulkSharingEnabled ? '#c9a84c' : '#ccc', cursor: bulkPriceTooLow ? 'not-allowed' : 'pointer', opacity: bulkPriceTooLow ? 0.5 : 1 }}
            >
              <span
                className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                style={{ left: bulkSharingEnabled ? '22px' : '2px' }}
              />
            </button>
          </div>
          {bulkPriceTooLow ? (
            <p className="tw-text-xs tw-mt-0 tw-mb-2" style={{ color: '#c9a84c' }}>Share & Earn is available for items $10.00 and above</p>
          ) : (
            <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-2">Let others promote your listing and earn a commission on sales</p>
          )}
          {bulkSharingEnabled && (
            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Commission Amount</label>
              <div className="tw-relative">
                <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)' }}>$</span>
                <input
                  type="text"
                  value={item.shareCommission}
                  onChange={function (e) {
                    var val = e.target.value;
                    var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
                    if (val === '' || re.test(val)) {
                      var maxComm = Math.floor(Number(item.price || 0) * 0.90 * 100) / 100;
                      var numVal = Number(val);
                      if (val !== '' && numVal > maxComm) { val = String(maxComm); numVal = maxComm; }
                      self.updateBatchItem(idx, 'shareCommission', val === '' ? '' : numVal || val);
                    }
                  }}
                  onBlur={function () {
                    var num = Number(item.shareCommission);
                    if (!num || num < 1) { self.updateBatchItem(idx, 'shareCommission', 1); }
                  }}
                  placeholder="1.00"
                  style={Object.assign({}, inputStyle, { paddingLeft: '24px' })}
                  className="focus:tw-border-brand-gold"
                />
              </div>
              {item.price ? (
                <p className="tw-text-xs tw-mt-1 tw-mb-0" style={{ color: '#c9a84c' }}>Marketer earns ${Math.max(1, Number(item.shareCommission) || 1).toFixed(2)} per sale (minimum $1.00)</p>
              ) : null}
              {item.price ? (
                <p className="tw-text-xs tw-text-[#888] tw-mt-0.5 tw-mb-0">Max: ${(Math.floor(Number(item.price) * 0.90 * 100) / 100).toFixed(2)} (your 90% earnings)</p>
              ) : null}
              <p className="tw-text-xs tw-text-[#888] tw-mt-1 tw-mb-0">Deducted from your earnings when someone buys through a share link</p>
            </div>
          )}
        </div>
          );
        })()}

        {/* Open to Offers */}
        <div className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Open to Offers</label>
            <button
              type="button"
              onClick={function () { self.updateBatchItem(idx, 'acceptOffers', !item.acceptOffers); }}
              className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-colors tw-flex-shrink-0"
              style={{ backgroundColor: item.acceptOffers ? '#c9a84c' : '#ccc' }}
            >
              <span
                className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                style={{ left: item.acceptOffers ? '22px' : '2px' }}
              />
            </button>
          </div>
          <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-0">Allow buyers to make offers below your listed price</p>
        </div>

        {/* Add Photos — collapsible */}
        <div className="tw-mb-4">
          <button
            type="button"
            onClick={function () { self.updateBatchItem(idx, 'showPhotos', !item.showPhotos); }}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-font-semibold hover:tw-underline"
            style={{ color: '#c9a84c' }}
          >
            <i className={'fa ' + (item.showPhotos ? 'fa-chevron-down' : 'fa-camera')} style={{ fontSize: '12px' }} />
            {item.showPhotos ? 'Hide photos' : 'Add photos'}
            <span className="tw-text-xs tw-font-normal tw-text-[#999]">(optional)</span>
            {!item.showPhotos && item.photos.length > 0 && (
              <span
                className="tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-ml-1"
                style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}
              >
                {item.photos.length}
              </span>
            )}
          </button>
          {item.showPhotos && (
            <div className="tw-mt-3">
              <p className="tw-text-xs tw-mb-3 tw-mt-0" style={{ color: isDark ? '#4b5563' : '#bbb' }}>
                Up to 4 photos &middot; JPG, PNG, GIF, or WebP &middot; 10 MB max
              </p>
              {self.state.photoError && (
                <p className="tw-text-red-500 tw-text-sm tw-mb-3 tw-mt-0">{self.state.photoError}</p>
              )}
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {item.photos.map(function (photo, i) {
                  return (
                    <div key={i} className="tw-relative tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                      <img src={photo.preview} alt={'Photo ' + (i + 1)} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                      <button
                        type="button"
                        onClick={function () { self.removePhoto(idx, i); }}
                        className="tw-absolute tw-top-2 tw-right-2 tw-w-7 tw-h-7 tw-rounded-full tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                        style={{ lineHeight: 1 }}
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
                {self.state.processingPhoto && (
                  <div className="tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                      <div className="tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-[#e0e0e0]" style={{ borderTopColor: '#c9a84c', animation: 'spin 0.8s linear infinite' }} />
                      <span className="tw-text-[10px] tw-text-[#999]">Processing</span>
                    </div>
                  </div>
                )}
                {!self.state.processingPhoto && item.photos.length < 4 && (
                  <label
                    className="tw-aspect-square tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"
                    style={{ borderColor: isDark ? '#444' : '#d0d0d0', backgroundColor: isDark ? '#0f0f0f' : '#f9fafb' }}
                    onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                    onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#444' : '#d0d0d0'; }}
                  >
                    <i className="fa fa-camera tw-text-2xl tw-text-[#bbb] tw-mb-1" />
                    <span className="tw-text-xs tw-text-[#999] tw-font-medium">Add Photo</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={function (e) { self.addPhoto(e); }}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Next button */}
        <button
          type="button"
          onClick={function () { self.goToNextDetailCard(); }}
          className="tw-w-full tw-font-bold tw-py-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none"
          style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
          onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {idx < batch.length - 1 ? 'Next Card \u2192' : 'Continue'}
        </button>
      </div>
    );
  }

  // ─── Step 3: Review ─────────────────────────────────────

  renderStep3() {
    var batch = this.state.batch;
    var submitting = this.state.submitting;
    var submitError = this.state.submitError;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var self = this;

    return (
      <div>
        <button
          type="button"
          onClick={function () { self.setState({ step: 2, currentCardIndex: batch.length - 1 }); }}
          className="tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-mb-5 tw-p-0"
          style={{ color: '#999', transition: 'color 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={function (e) { e.currentTarget.style.color = '#999'; }}
        >
          &larr; Back to details
        </button>

        <div className="tw-text-center tw-mb-6">
          <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            Review All Listings
          </h2>
          <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
            Double-check everything before creating your listings
          </p>
        </div>

        {/* Card list */}
        <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-6">
          {batch.map(function (item, i) {
            var condLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === item.condition; });
            return (
              <div
                key={i}
                className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-xl"
                style={{ backgroundColor: isDark ? '#141414' : '#fafafa', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', transition: 'border-color 0.15s ease' }}
                onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
              >
                {item.cardImage && (
                  <img src={item.cardImage} alt={item.title} className="tw-w-14 tw-rounded tw-flex-shrink-0" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
                )}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-bold tw-mb-0.5 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                    {item.title}
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-mb-0.5">
                    {condLabel && (
                      <span
                        className="tw-inline-block tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full"
                        style={{ backgroundColor: 'rgba(201,168,76,0.12)', color: '#c9a84c' }}
                      >
                        {condLabel.label}
                      </span>
                    )}
                    {Number(item.quantity) > 1 && (
                      <span className="tw-text-[10px] tw-font-semibold tw-text-[#999] tw-px-1.5 tw-py-0.5 tw-rounded-full" style={{ backgroundColor: isDark ? '#252525' : '#f0f0f0' }}>
                        Qty: {item.quantity}
                      </span>
                    )}
                    {item.photos.length > 0 && (
                      <span className="tw-text-[10px] tw-text-[#999]">
                        <i className="fa fa-camera tw-mr-0.5" />{item.photos.length}
                      </span>
                    )}
                  </div>
                  <p className="tw-text-base tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                    ${Number(item.price).toFixed(2)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={function () { self.editCardFromReview(i); }}
                  className="tw-text-xs tw-font-semibold tw-bg-transparent tw-rounded-lg tw-px-3 tw-py-1.5 tw-cursor-pointer tw-flex-shrink-0"
                  style={{ color: '#c9a84c', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', transition: 'border-color 0.15s ease' }}
                  onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                  onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
                >
                  Edit
                </button>
              </div>
            );
          })}
        </div>

        {/* Error message */}
        {submitError && (
          <p className="tw-text-red-500 tw-text-sm tw-font-medium tw-mb-3 tw-mt-0">{submitError}</p>
        )}

        {/* Payout setup banner */}
        {self.state.hasPayoutMethod === false && (
          <a href="/settings?tab=payments" target="_blank" rel="noopener noreferrer" className="tw-block tw-mb-5 tw-p-4 tw-rounded-xl hover:tw-opacity-90 tw-transition-opacity" style={{ backgroundColor: 'rgba(196,163,90,0.08)', border: '1px solid rgba(196,163,90,0.25)' }}>
            <div className="tw-flex tw-items-start tw-gap-3">
              <i className="fa fa-info-circle tw-text-lg" style={{ lineHeight: '20px', color: '#c4a35a' }} />
              <div>
                <p className="tw-text-sm tw-font-semibold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Set up payouts to get paid</p>
                <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#aaa' : '#666' }}>
                  You can list items now! Connect Stripe or PayPal in your payment settings to receive earnings when your items sell.
                </p>
              </div>
              <i className="fa fa-external-link tw-text-xs tw-flex-shrink-0" style={{ color: '#c4a35a', marginTop: '3px' }} />
            </div>
          </a>
        )}

        {/* Submit bar */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-xl" style={{ backgroundColor: isDark ? '#141414' : '#fafafa', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
          <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#9ca3af' : '#666' }}>
            <span className="tw-font-bold" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>{batch.length}</span> card{batch.length !== 1 ? 's' : ''} ready
          </p>
          <button
            type="button"
            onClick={function () { self.submitAll(); }}
            disabled={submitting}
            className="tw-font-bold tw-py-2.5 tw-px-8 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
            onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
            onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            {submitting ? 'Creating Listings...' : 'Create All Listings'}
          </button>
        </div>
      </div>
    );
  }

  // ─── Results ────────────────────────────────────────────

  renderSuccessModal() {
    var results = this.state.results;
    var successCount = results.results ? results.results.length : 0;
    var totalQty = 0;
    if (results.results) {
      results.results.forEach(function (r) { totalQty += (r.quantity || 1); });
    }
    var errorCount = results.errors ? results.errors.length : 0;
    var user = Auth.getProfile();
    var isDark = document.documentElement.classList.contains('tw-dark');

    return (
      <>
        <div
          className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-50"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={function () { this.props.history.push('/shop/' + user.username); }.bind(this)}
        />
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4 tw-pointer-events-none">
          <div
            className="modal-enter tw-rounded-2xl tw-shadow-xl tw-w-full tw-max-w-sm tw-p-6 tw-text-center tw-pointer-events-auto"
            style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
          >

            {/* Checkmark */}
            <div className="tw-flex tw-justify-center tw-mb-4">
              <div className="tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: '#22c55e' }}>
                <i className="fa fa-check tw-text-white tw-text-2xl" />
              </div>
            </div>

            <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
              {successCount} Listing{successCount !== 1 ? 's' : ''} Created!
              {totalQty > successCount ? ' (' + totalQty + ' copies)' : ''}
            </h2>
            {errorCount > 0 && (
              <p className="tw-text-sm tw-text-red-500 tw-mb-1">
                {errorCount} listing{errorCount !== 1 ? 's' : ''} failed
              </p>
            )}
            <p className="tw-text-sm tw-mb-5" style={{ color: isDark ? '#9ca3af' : '#666' }}>
              Your cards are now live on the marketplace
            </p>

            {/* Payout reminder */}
            {this.state.hasPayoutMethod === false && (
              <div className="tw-rounded-lg tw-p-3 tw-mb-5 tw-text-left" style={{ backgroundColor: 'rgba(196,163,90,0.08)', border: '1px solid rgba(196,163,90,0.25)' }}>
                <p className="tw-text-sm tw-font-semibold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                  <i className="fa fa-info-circle tw-mr-1.5" style={{ color: '#c4a35a' }} />
                  Set up payouts to get paid
                </p>
                <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#aaa' : '#666' }}>
                  Connect Stripe or add your PayPal email so you can receive earnings when your items sell.
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="tw-flex tw-flex-col tw-gap-2.5">
              <button
                type="button"
                onClick={function () { this.props.history.push('/shop/' + user.username); }.bind(this)}
                className="tw-w-full tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none"
                style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
                onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
                onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                View Shop
              </button>
              {this.state.hasPayoutMethod === false && (
                <a
                  href="/settings?tab=payments"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-block tw-w-full tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-text-center tw-transition-colors"
                  style={{ color: '#c9a84c', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', transition: 'border-color 0.15s ease' }}
                  onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                  onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
                >
                  Set Up Payouts
                </a>
              )}
              <button
                type="button"
                onClick={function () { this.setState({ results: null, batch: [], step: 1, currentCardIndex: 0 }); }.bind(this)}
                className="tw-w-full tw-py-2 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none tw-bg-transparent tw-transition-colors"
                style={{ color: '#999' }}
              >
                List More Cards
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Render ─────────────────────────────────────────────

  render() {
    var step = this.state.step;
    var results = this.state.results;
    var isDark = document.documentElement.classList.contains('tw-dark');

    return (
      <div className="tw-min-h-[calc(100vh-64px)]" style={{ backgroundColor: isDark ? '#111' : '#fff' }}>
        <Helmet><title>Bulk Sell | Primal Marketplace</title></Helmet>

        <div className="tw-max-w-lg tw-mx-auto tw-px-4 tw-pt-8 tw-pb-2">
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
            <h1 className="tw-text-2xl tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
              Bulk Sell
            </h1>
            <Link
              to="/sell"
              className="tw-text-sm tw-font-medium tw-no-underline tw-px-3 tw-py-1.5 tw-rounded-lg tw-transition-colors"
              style={{ color: '#c9a84c', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
              onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
              onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
            >
              <i className="fa fa-plus tw-mr-1" />
              Single Listing
            </Link>
          </div>

          {this.renderStepIndicator()}

          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}

          {/* Bottom padding */}
          <div className="tw-h-12" />
        </div>

        {/* Success Modal */}
        {results && this.renderSuccessModal()}
      </div>
    );
  }
}

export default withAuth(BulkPost);
